import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'

import { PageLayout } from '@components/layouts/PageLayout'
import { Button } from '@atoms'
import { SelectField } from '@components/molecules/SelectField'
import Icon, { type IconTypes } from '@components/utils/Icon'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/atoms/Tooltip'
import { Sheet, SheetContent, SheetTitle } from '@components/atoms/Sheet'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@components/atoms/DropdownMenu'

/**
 * Group Inbox: generic preview (design-phase prototype)
 *
 * A leader-facing combined inbox that surfaces email, SMS and WhatsApp in one
 * view. This is the shareable, client-neutral twin of `GroupInboxIndex.tsx`:
 * same components and layout, but the sample data is a fictional grassroots
 * campaign ("Common Ground") with local chapters handling supporter messages
 * about town halls, canvassing, a membership drive and election-day volunteering.
 * No real organisation, people or content, so it is safe to demo to any client. There is
 * no model or GraphQL behind it; it exists to explore the redesign inside the
 * real app shell using real v2 components. The "Design tools" overlay at the
 * bottom-left is dev-only.
 */

type Channel = 'email' | 'sms' | 'whatsapp'
type Status = 'awaiting' | 'responded'
type Audience = 'supporter' | 'guest'

type EmailMessage = { from: 'them' | 'us'; name: string; time: string; html: string; attachments?: string[] }
type ChatBubble = { dir: 'in' | 'out'; text: string }

type Conversation = {
  id: number
  name: string
  handle: string
  subject: string
  preview: string
  minsAgo: number
  time: string
  status: Status
  channel: Channel
  audience: Audience
  group: string
  assignee: string
  emails?: EmailMessage[]
  bubbles?: ChatBubble[]
}

const CHANNELS: Record<Channel, { label: string; icon: IconTypes; bg: string; fg: string }> = {
  email: { label: 'Email', icon: 'mail-01', bg: 'tw:bg-primary-50', fg: 'tw:text-primary-600' },
  sms: { label: 'SMS', icon: 'message-chat-square', bg: 'tw:bg-blue-50', fg: 'tw:text-blue-600' },
  whatsapp: { label: 'WhatsApp', icon: 'message-circle', bg: 'tw:bg-success-50', fg: 'tw:text-success-600' },
}

// ---- product-facing strings (dev tool labels stay inline) ----
type Strings = {
  inbox: string; search: string; clearSearch: string; filters: string; channel: string; status: string; group: string; at: string
  anyStatus: string; awaiting: string; responded: string; clearFilters: string; filterByGroup: string
  filterByAssignee: string; allGroups: string; anyAssignee: string; supporterInbox: string; guestInbox: string; guest: string; of: string
  sortNewest: string; sortOldest: string; reply: string; internalNote: string
  writeReply: string; typeMessage: string; noteVisible: string; send: string; sendResponded: string
  saveDraft: string; smsMeta: string; whatsappMeta: string; conversationDetails: string; viewSupporter: string
  close: string; sampleProfile: string; contact: string; supporter: string
  viewGuest: string; openSupporter: string; openGuest: string; markResponded: string; markAwaiting: string
  assignee: string; owner: string; assignTo: string; quickActions: string; addToGroup: string; addTag: string
  logContact: string; addToCallList: string; markSpam: string; recentActivity: string; unassigned: string
  onThisThread: string; yourTeam: string; guestBannerTitle: string; hideActions: string; showActions: string
  back: string; noMatch: string; selectConversation: string
  channelReply: (ch: string) => string; replied: (name: string) => string; via: (ch: string) => string
  assignedTo: (a: string) => string; to: (x: string) => string; guestBannerBody: (name: string) => string
}

const t: Strings = {
  inbox: 'Inbox', search: 'Search conversations', clearSearch: 'Clear search', filters: 'Filters',
  channel: 'Channel', status: 'Status', group: 'Group', at: 'at', anyStatus: 'Any status', awaiting: 'Awaiting response', responded: 'Responded',
  clearFilters: 'Clear filters', filterByGroup: 'Filter by group', filterByAssignee: 'Filter by assignee', allGroups: 'All groups', anyAssignee: 'Anyone',
  supporterInbox: 'Supporter inbox', guestInbox: 'Guest inbox', guest: 'Guest', of: 'of',
  sortNewest: 'Newest first', sortOldest: 'Oldest first',
  reply: 'Reply', internalNote: 'Internal note', writeReply: 'Write your reply…', typeMessage: 'Type a message…',
  noteVisible: 'Visible to your team only…', send: 'Send', sendResponded: 'Send and mark responded', saveDraft: 'Save draft',
  smsMeta: '152 / 160 chars · 1 SMS', whatsappMeta: 'WhatsApp · within 24h window',
  conversationDetails: 'Conversation details', viewSupporter: 'View supporter details', viewGuest: 'View guest details',
  close: 'Close', sampleProfile: 'Sample data, not a live supporter record.', contact: 'Contact', supporter: 'Supporter',
  openSupporter: 'Open supporter details', openGuest: 'Open guest details',
  markResponded: 'Mark as responded', markAwaiting: 'Mark as awaiting response',
  assignee: 'Assignee', owner: 'Owner', assignTo: 'Assign to…', quickActions: 'Quick actions',
  addToGroup: 'Add to group', addTag: 'Add tag', logContact: 'Log contact', addToCallList: 'Add to call list', markSpam: 'Mark as spam',
  recentActivity: 'Recent activity', unassigned: 'Unassigned', onThisThread: 'on this thread', yourTeam: 'Your team',
  guestBannerTitle: 'Guest, not in a group yet', hideActions: 'Hide actions', showActions: 'Show actions', back: 'Back',
  noMatch: 'No conversations match these filters.', selectConversation: 'Select a conversation',
  channelReply: (ch) => `${ch} reply`, replied: (name) => `${name} replied`, via: (ch) => `via ${ch}`,
  assignedTo: (a) => `Assigned to ${a}`, to: (x) => `to ${x}`,
  guestBannerBody: (name) => `${name} reached out to the group directly and isn't a member yet. A warm intro to what you do and how to join works well here.`,
}

// ---- guest colour: derived from a single hex (configurable via the colour picker),
// kept distinct from the amber "awaiting" badge ----
function shadeDarken(hex: string, amount: number): string {
  const n = parseInt(hex.replace('#', ''), 16)
  const r = Math.round(((n >> 16) & 255) * (1 - amount))
  const g = Math.round(((n >> 8) & 255) * (1 - amount))
  const b = Math.round((n & 255) * (1 - amount))
  return `rgb(${r}, ${g}, ${b})`
}
function shadeAlpha(hex: string, a: number): string {
  const n = parseInt(hex.replace('#', ''), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
}
type GuestStyles = {
  pill: CSSProperties; rowBorder: CSSProperties; banner: CSSProperties
  bannerTitle: CSSProperties; bannerIcon: CSSProperties; tab: CSSProperties; count: CSSProperties
}
function guestStyles(hex: string): GuestStyles {
  return {
    pill: { backgroundColor: shadeAlpha(hex, 0.14), color: shadeDarken(hex, 0.4) },
    rowBorder: { borderLeftColor: hex },
    banner: { backgroundColor: shadeAlpha(hex, 0.08), borderColor: shadeAlpha(hex, 0.35) },
    bannerTitle: { color: shadeDarken(hex, 0.4) },
    bannerIcon: { color: hex },
    tab: { borderBottomColor: hex, color: shadeDarken(hex, 0.35) },
    count: { backgroundColor: hex, color: '#ffffff' },
  }
}

const GROUPS = [
  'Common Ground Riverside', 'Common Ground Northgate', 'Common Ground Westfield', 'Common Ground Oakdale',
  'Common Ground Harbourview', 'Common Ground Greenfield', 'Common Ground Millbrook', 'Common Ground Fairview',
]
const ASSIGNEES = [
  'You', 'Priya', 'Daniel', 'Hannah', 'Marcus', 'Sofia', 'Leah', 'Omar', 'Unassigned',
]

const CONVERSATIONS: Conversation[] = [
  {
    id: 1, name: 'Maya Thompson', handle: 'maya.thompson@gmail.com',
    subject: 'RSVP: Riverside town hall, Sat 6 June',
    preview: 'Perfect, is there parking near the community hall?',
    minsAgo: 14, time: '14m', status: 'awaiting',
    channel: 'email', audience: 'supporter', group: 'Common Ground Riverside', assignee: 'You',
    emails: [
      { from: 'them', name: 'Maya Thompson', time: '1h', html: '<p>Hi there,</p><p>I\'d love to come to the <b>Riverside town hall on Saturday 6 June at 5pm</b> at the community hall. Can I bring two friends who are curious about the campaign?</p><p>Maya</p>' },
      { from: 'us', name: 'You', time: '35m', html: '<p>Hi Maya,</p><p>Absolutely, all welcome. Just sign in at the door so we have a headcount. See you Saturday!</p>' },
      { from: 'them', name: 'Maya Thompson', time: '14m', html: '<p>Perfect, is there parking near the community hall?</p>' },
    ],
  },
  {
    id: 2, name: 'Ravi Anand', handle: '+1 555 0182',
    subject: 'Re: Open chapter meeting, 4 June', preview: 'Do I need to bring anything?',
    minsAgo: 28, time: '28m', status: 'responded',
    channel: 'sms', audience: 'supporter', group: 'Common Ground Westfield', assignee: 'Unassigned',
    bubbles: [
      { dir: 'in', text: "I'll be at the open chapter meeting on 4 June, what time does it start?" },
      { dir: 'out', text: 'Great to have you Ravi! Doors open at 6:15pm at the usual venue.' },
      { dir: 'in', text: 'Do I need to bring anything?' },
    ],
  },
  {
    id: 3, name: 'Grace Mbeki', handle: '+1 555 7788',
    subject: 'Photos from the weekend rally', preview: "These are great, thanks Grace! We'll credit you.",
    minsAgo: 70, time: '1h', status: 'responded',
    channel: 'whatsapp', audience: 'supporter', group: 'Common Ground Harbourview', assignee: 'Daniel',
    bubbles: [
      { dir: 'in', text: 'Sharing some photos from the Harbourview rally on Saturday' },
      { dir: 'in', text: 'Use whatever you like for the socials' },
      { dir: 'out', text: "These are great, thanks Grace! We'll credit you when they go up." },
    ],
  },
  {
    id: 4, name: 'Daniel Foster', handle: 'd.foster@outlook.com',
    subject: 'URGENT: has the Oakdale meeting venue changed?',
    preview: 'Thank you, please send it so I can forward to everyone tonight.',
    minsAgo: 95, time: '2h', status: 'awaiting',
    channel: 'email', audience: 'supporter', group: 'Common Ground Oakdale', assignee: 'You',
    emails: [
      { from: 'them', name: 'Daniel Foster', time: '3h', html: '<p>I told <b>40 people</b> the old address for the chapter meeting. Has the venue moved? I need to know tonight.</p><p>Daniel</p>' },
      { from: 'us', name: 'You', time: '2h', html: '<p>Hi Daniel, sorry for the confusion. Yes, it moved to the Oakdale Library hall. I\'m attaching the updated flyer now.</p>', attachments: ['oakdale-meeting-updated.pdf'] },
      { from: 'them', name: 'Daniel Foster', time: '2h', html: '<p>Thank you, please send it so I can forward to everyone tonight.</p>' },
    ],
  },
  {
    id: 6, name: 'Liam Carter', handle: '+1 555 3321',
    subject: 'Carpool to the regional rally, Friday', preview: 'Outside the station at 5pm, how many should I expect?',
    minsAgo: 150, time: '3h', status: 'awaiting',
    channel: 'sms', audience: 'supporter', group: 'Common Ground Northgate', assignee: 'Hannah',
    bubbles: [
      { dir: 'in', text: 'Driving to the regional rally on Friday, I have 3 spare seats' },
      { dir: 'out', text: "Brilliant, thanks! I'll share with the Northgate group. Where should people meet you?" },
      { dir: 'in', text: 'Outside the station at 5pm, how many should I expect?' },
    ],
  },
  {
    id: 8, name: 'Noah Bennett', handle: '+1 555 1199',
    subject: 'Signage for the town hall', preview: "Sure, I'll have the A3 to you tomorrow.",
    minsAgo: 240, time: '4h', status: 'awaiting',
    channel: 'whatsapp', audience: 'supporter', group: 'Common Ground Riverside', assignee: 'Unassigned',
    bubbles: [
      { dir: 'in', text: 'Banner for the town hall is ready, sending the file' },
      { dir: 'out', text: 'Looks perfect, could you also do a smaller A3 for the door?' },
      { dir: 'in', text: "Sure, I'll have the A3 to you tomorrow." },
    ],
  },
  {
    id: 9, name: 'Sofia Reyes', handle: 'sofia.reyes@gmail.com',
    subject: 'House meeting for the membership drive',
    preview: 'Great, can we aim for the week of 9 June?',
    minsAgo: 420, time: '7h', status: 'awaiting',
    channel: 'email', audience: 'supporter', group: 'Common Ground Northgate', assignee: 'Priya',
    emails: [
      { from: 'them', name: 'Sofia Reyes', time: '8h', html: '<p>Hi, can I host a small house meeting for my neighbours to talk about the membership drive?</p><p>Sofia</p>' },
      { from: 'us', name: 'Priya', time: '7h', html: '<p>We\'d love that, Sofia! I\'ll send a host pack and can join by video if useful.</p>' },
      { from: 'them', name: 'Sofia Reyes', time: '7h', html: '<p>Great, can we aim for the week of 9 June?</p>' },
    ],
  },
  {
    id: 11, name: 'Elena Novak', handle: 'elena.novak@gmail.com',
    subject: 'Volunteering for election day, 7-8 June',
    preview: 'Door-knocking on the Sunday works for me.',
    minsAgo: 600, time: '10h', status: 'awaiting',
    channel: 'email', audience: 'supporter', group: 'Common Ground Millbrook', assignee: 'You',
    emails: [
      { from: 'them', name: 'Elena Novak', time: '11h', html: '<p>Hi, I\'d like to help out on election day, <b>7-8 June</b>. What do you need?</p><p>Elena</p>' },
      { from: 'us', name: 'You', time: '10h', html: '<p>Wonderful. We need door-knockers and people at the stall. Which are you up for?</p>' },
      { from: 'them', name: 'Elena Novak', time: '10h', html: '<p>Door-knocking on the Sunday works for me.</p>' },
    ],
  },
  {
    id: 7, name: 'Hannah Cole', handle: 'hannah.cole@gmail.com',
    subject: 'Helping at the candidate forum',
    preview: 'Count me in, happy to help on the registration desk.',
    minsAgo: 1500, time: 'Yesterday', status: 'responded',
    channel: 'email', audience: 'supporter', group: 'Common Ground Riverside', assignee: 'You',
    emails: [
      { from: 'them', name: 'Hannah Cole', time: 'Yesterday', html: '<p>Count me in for the <b>candidate forum</b>. Happy to help on the registration desk.</p><p>Hannah</p>' },
      { from: 'us', name: 'You', time: 'Yesterday', html: '<p>Amazing, thanks Hannah. I\'ve put you on the welcome desk from 5pm.</p>' },
    ],
  },
  {
    id: 10, name: 'Paul Garcia', handle: '+1 555 7741',
    subject: 'Volunteer day at Riverside Park', preview: 'Perfect, see you at 10am.',
    minsAgo: 1700, time: 'Yesterday', status: 'responded',
    channel: 'sms', audience: 'supporter', group: 'Common Ground Riverside', assignee: 'Daniel',
    bubbles: [
      { dir: 'in', text: 'Got my spot for the volunteer day at Riverside Park, thanks!' },
      { dir: 'out', text: 'Perfect, see you at 10am. Bring a friend if you like.' },
    ],
  },
  {
    id: 13, name: 'Sam Okafor', handle: 's.okafor@riversidegazette.com',
    subject: 'Press: comment on the Oakdale campaign',
    preview: 'Thanks, my deadline is Friday afternoon.',
    minsAgo: 2600, time: '2d', status: 'awaiting',
    channel: 'email', audience: 'guest', group: 'Common Ground Oakdale', assignee: 'You',
    emails: [
      { from: 'them', name: 'Sam Okafor', time: '2d', html: '<p>Hello, I\'m a reporter at the <b>Riverside Gazette</b> covering the local campaign. Could I get a short comment?</p><p>Sam</p>' },
      { from: 'us', name: 'You', time: '2d', html: '<p>Happy to help. Our coordinator can give a quote, I\'ve copied her here. What\'s your deadline?</p>' },
      { from: 'them', name: 'Sam Okafor', time: '2d', html: '<p>Thanks, my deadline is Friday afternoon.</p>' },
    ],
  },
  {
    id: 5, name: 'Fiona Walsh', handle: 'f.walsh@gmail.com',
    subject: 'Renew my membership',
    preview: 'Welcome back! Here is the renewal link, it takes two minutes.',
    minsAgo: 3100, time: '2d', status: 'responded',
    channel: 'email', audience: 'supporter', group: 'Common Ground Greenfield', assignee: 'Hannah',
    emails: [
      { from: 'them', name: 'Fiona Walsh', time: '2d', html: '<p>Hi, I\'d like to renew my membership for this year. How do I do it?</p><p>Fiona</p>' },
      { from: 'us', name: 'Hannah', time: '2d', html: '<p>Welcome back, Fiona! Here is the renewal link, it takes two minutes. Thanks for keeping the Greenfield chapter growing.</p>' },
    ],
  },
  {
    id: 14, name: 'Lara Hill', handle: 'lara@thecornercafe.com',
    subject: 'Can my cafe host a Common Ground meetup?',
    preview: 'We run a cafe near the town square and would be glad to host an event.',
    minsAgo: 4300, time: '3d', status: 'awaiting',
    channel: 'email', audience: 'guest', group: 'Common Ground Westfield', assignee: 'Unassigned',
    emails: [
      { from: 'them', name: 'Lara Hill', time: '3d', html: '<p>Hi, we run a cafe near the town square and would be glad to host a Common Ground meetup or coffee morning. Who can I talk to?</p><p>Lara</p>' },
    ],
  },
  {
    id: 12, name: 'Jordan Blake', handle: 'jordan.blake@gmail.com',
    subject: 'How do I get involved?',
    preview: "I saw your stall at the market and I'm not a member yet…",
    minsAgo: 5000, time: '3d', status: 'awaiting',
    channel: 'email', audience: 'guest', group: 'Common Ground Northgate', assignee: 'Unassigned',
    emails: [
      { from: 'them', name: 'Jordan Blake', time: '3d', html: "<p>Hi there, I saw your stall at the market on Saturday and I'm not a member yet. How do I get involved and come to the next meeting?</p><p>Jordan</p>" },
    ],
  },
  {
    id: 15, name: '+1 555 9920', handle: 'No name on file',
    subject: 'Unsubscribe', preview: "No problem, you've been removed from our list.",
    minsAgo: 22000, time: '15 May', status: 'responded',
    channel: 'sms', audience: 'guest', group: 'Common Ground Riverside', assignee: 'Unassigned',
    bubbles: [
      { dir: 'in', text: "Please stop texting me, I'm not interested. Thanks." },
      { dir: 'out', text: "No problem, you've been removed from our list. Apologies for the disturbance." },
    ],
  },
]

function statusText(status: Status): string {
  return status === 'awaiting' ? t.awaiting : t.responded
}

type SortKey = 'newest' | 'oldest'
type Filters = { channels: Channel[]; status?: Status; group?: string; assignee?: string }
type Density = 'comfortable' | 'compact'
// How the list signals awaiting-response vs responded. Explored via the design tools.
// Full-row background fills read as dated and hurt scanning a mixed list; weight/
// contrast plus a small status marker scans better, and status ideally lives in the
// view/filter so "done" largely leaves the queue.
//  - icon:     small leading marker (amber = awaiting, green check = done), no fill
//  - faded:    weight and contrast. Awaiting bolder/graphite, responded lighter (Gmail-ish)
//  - tint:     soft amber background on awaiting rows (glanceable but heaviest)
//  - pillOnly: no row treatment, rely on the status pill alone (minimal)
type StatusStyle = 'icon' | 'faded' | 'tint' | 'pillOnly'

const PAGE_SIZE = 7

const ICON_BTN =
  'tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:text-gray-500 tw:hover:border-primary-400 tw:hover:text-primary-600'

function ChannelGlyph({ channel, size = 'md' }: { channel: Channel; size?: 'sm' | 'md' }) {
  const ch = CHANNELS[channel]
  const box = size === 'md' ? 'tw:h-9 tw:w-9' : 'tw:h-8 tw:w-8'
  return (
    <span role="img" aria-label={ch.label} className={clsx('tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg', box, ch.bg)} title={ch.label}>
      <Icon type={ch.icon} className={clsx('tw:h-4.5 tw:w-4.5', ch.fg)} aria-hidden />
    </span>
  )
}

function StatusPill({ c }: { c: Conversation }) {
  const cls = c.status === 'awaiting' ? 'tw:bg-warning-50 tw:text-warning-700' : 'tw:bg-success-50 tw:text-success-700'
  return (
    <span className={clsx('tw:whitespace-nowrap tw:rounded-full tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium', cls)}>
      {statusText(c.status)}
    </span>
  )
}

function GuestPill({ gs }: { gs: GuestStyles }) {
  return (
    <span style={gs.pill} className="tw:whitespace-nowrap tw:rounded-full tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium">
      {t.guest}
    </span>
  )
}

// Relative times like "14m" / "Yesterday" are what the list shows; the thread
// header reveals the exact moment on hover. There is no real timestamp behind the
// sample data, so derive one from minsAgo against the viewer's clock + locale.
function fullTimestamp(minsAgo: number, atWord: string): string {
  const dt = new Date(Date.now() - minsAgo * 60000)
  const date = dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
  const time = dt.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s/g, '')
  const tz = new Intl.DateTimeFormat('en-AU', { timeZoneName: 'short' })
    .formatToParts(dt)
    .find((p) => p.type === 'timeZoneName')?.value
  return `${date} ${atWord} ${time}${tz ? ` ${tz}` : ''}`
}

// Best-effort parse of the sample relative-time strings ("14m", "2h", "3d",
// "Yesterday") back into minutes, so any short time can reveal a full timestamp
// on hover even when the caller only has the string. Absolute dates ("15 May")
// return null and are rendered plain, without a tooltip.
function relativeToMins(time: string): number | null {
  const m = time.match(/^(\d+)\s*([mhd])$/)
  if (m) {
    const n = Number(m[1])
    return m[2] === 'm' ? n : m[2] === 'h' ? n * 60 : n * 1440
  }
  if (time === 'Yesterday') return 1440
  return null
}

// Shared short-time display: shows the relative time, with the exact timestamp on
// hover. Pass minsAgo when known (accurate); otherwise it's parsed from `time`.
function ShortTime({ time, minsAgo, atWord, className }: { time: string; minsAgo?: number; atWord: string; className?: string }) {
  const mins = minsAgo ?? relativeToMins(time)
  if (mins == null) return <span className={className}>{time}</span>
  const full = fullTimestamp(mins, atWord)
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={clsx('tw:cursor-default tw:underline tw:decoration-dotted tw:decoration-gray-400 tw:underline-offset-2', className)}>
            {time}
            <span className="tw:sr-only"> ({full})</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>{full}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

const GroupInboxIndexGeneric = () => {
  const [audience, setAudience] = useState<Audience>('supporter')
  const [filters, setFilters] = useState<Filters>({ channels: [], status: 'awaiting' })
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortKey>('newest')
  const [selectedId, setSelectedId] = useState<number>(1)
  const [railOpen, setRailOpen] = useState(true)
  const [composerMode, setComposerMode] = useState<'reply' | 'note'>('reply')
  const [page, setPage] = useState(1)
  const [readingPane, setReadingPane] = useState(false)
  const [view, setView] = useState<'list' | 'thread'>('list')
  const [profileOpen, setProfileOpen] = useState(false)

  // Design-exploration toggles (floating dev overlay)
  const [showDesign, setShowDesign] = useState(false)
  const [density, setDensity] = useState<Density>('comfortable')
  const [showWhatsApp, setShowWhatsApp] = useState(false)
  const [showAssignee, setShowAssignee] = useState(false)
  const [statusStyle, setStatusStyle] = useState<StatusStyle>('icon')
  const [guestHex, setGuestHex] = useState('#EC4899')

  const gs = guestStyles(guestHex)
  const compact = density === 'compact'
  const channelOptions: Channel[] = showWhatsApp ? ['email', 'sms', 'whatsapp'] : ['email', 'sms']
  const sortLabels: Record<SortKey, string> = { newest: t.sortNewest, oldest: t.sortOldest }

  const visibleConversations = useMemo(
    () => CONVERSATIONS.filter((c) => showWhatsApp || c.channel !== 'whatsapp'),
    [showWhatsApp]
  )

  const toggleChannel = (ch: Channel) =>
    setFilters((f) => ({ ...f, channels: f.channels.includes(ch) ? f.channels.filter((c) => c !== ch) : [...f.channels, ch] }))

  const matchesFilters = (c: Conversation) => {
    if (filters.channels.length && !filters.channels.includes(c.channel)) return false
    if (filters.status && c.status !== filters.status) return false
    if (filters.group && c.group !== filters.group) return false
    if (filters.assignee && c.assignee !== filters.assignee) return false
    const q = search.trim().toLowerCase()
    if (q && !`${c.name} ${c.subject} ${c.preview}`.toLowerCase().includes(q)) return false
    return true
  }

  // Tab counts reflect the active filters, so they match what each inbox shows.
  const supporterCount = visibleConversations.filter((c) => c.audience === 'supporter' && matchesFilters(c)).length
  const guestCount = visibleConversations.filter((c) => c.audience === 'guest' && matchesFilters(c)).length

  const results = useMemo(() => {
    const list = visibleConversations.filter((c) => {
      if (c.audience !== audience) return false
      return matchesFilters(c)
    })
    list.sort((a, b) => (sort === 'oldest' ? b.minsAgo - a.minsAgo : a.minsAgo - b.minsAgo))
    return list
  }, [audience, filters, search, sort, visibleConversations])

  useEffect(() => {
    setPage(1)
  }, [audience, filters, search, sort, showWhatsApp])

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pageItems = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  const selected = results.find((c) => c.id === selectedId) ?? results[0]

  // When a thread opens (or you switch conversations), jump to the most recent message.
  const threadScrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = threadScrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [selected?.id, view, readingPane])

  const openConversation = (id: number) => {
    setSelectedId(id)
    if (!readingPane) setView('thread')
  }

  const renderRow = (c: Conversation) => (
    <ConversationRow key={c.id} c={c} selected={readingPane && selected?.id === c.id} compact={compact} statusStyle={statusStyle} gs={gs} onClick={() => openConversation(c.id)} />
  )

  const activeChips: Array<{ key: string; label: string; onRemove: () => void }> = [
    ...filters.channels.map((ch) => ({ key: `ch-${ch}`, label: `${t.channel}: ${CHANNELS[ch].label}`, onRemove: () => toggleChannel(ch) })),
    ...(filters.status ? [{ key: 'status', label: `${t.status}: ${statusText(filters.status)}`, onRemove: () => setFilters((f) => ({ ...f, status: undefined })) }] : []),
    ...(filters.group ? [{ key: 'group', label: `${t.group}: ${filters.group}`, onRemove: () => setFilters((f) => ({ ...f, group: undefined })) }] : []),
  ]
  const hasAnyFilter = filters.channels.length > 0 || !!filters.status || !!filters.group || !!filters.assignee

  const searchField = (
    <div className="tw:flex tw:items-center tw:rounded-lg tw:border tw:border-gray-400 tw:bg-white tw:px-3 tw:py-2 tw:focus-within:border-primary-400 tw:focus-within:ring-4 tw:ring-primary-100">
      <span className="tw:pr-2">
        <Icon type="search-lg" width={20} height={20} className="tw:text-gray-400" />
      </span>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t.search}
        aria-label={t.search}
        className="tw:w-full tw:bg-transparent tw:font-normal tw:text-base tw:text-gray-900 tw:placeholder-gray-500 tw:focus:outline-hidden"
      />
      {search && (
        <button onClick={() => setSearch('')} aria-label={t.clearSearch} className="tw:pl-2 tw:text-gray-400 tw:hover:text-gray-600">
          <Icon type="x-close" width={18} height={18} />
        </button>
      )}
    </div>
  )

  return (
    <PageLayout.Root>
      <PageLayout.Header heading={t.inbox} />
      <PageLayout.Body>
        <div className="tw:flex tw:flex-col tw:gap-4">
          {/* Toolbar */}
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
            <div className="tw:min-w-[16rem] tw:flex-1">{searchField}</div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button rank="secondary" leadingIcon="filter-lines">
                  {t.filters}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="tw:min-w-52">
                <DropdownMenuLabel>{t.channel}</DropdownMenuLabel>
                {channelOptions.map((ch) => (
                  <DropdownMenuCheckboxItem
                    key={ch}
                    checked={filters.channels.includes(ch)}
                    onCheckedChange={() => toggleChannel(ch)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    <Icon type={CHANNELS[ch].icon} className={clsx('tw:mr-2 tw:h-4 tw:w-4', CHANNELS[ch].fg)} />
                    {CHANNELS[ch].label}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel>{t.status}</DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={filters.status ?? 'all'}
                  onValueChange={(v) => setFilters((f) => ({ ...f, status: v === 'all' ? undefined : (v as Status) }))}
                >
                  <DropdownMenuRadioItem value="all">{t.anyStatus}</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="awaiting">{t.awaiting}</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="responded">{t.responded}</DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="tw:w-60">
              <SelectField
                hideLabel
                name="group-filter"
                label={t.filterByGroup}
                emptyOptionLabel={t.allGroups}
                options={GROUPS.map((g) => ({ value: g, label: g }))}
                value={filters.group}
                onValueChange={(v) => setFilters((f) => ({ ...f, group: v ?? undefined }))}
              />
            </div>

            {showAssignee && (
              <div className="tw:w-60">
                <SelectField
                  hideLabel
                  name="assignee-filter"
                  label={t.filterByAssignee}
                  emptyOptionLabel={t.anyAssignee}
                  options={ASSIGNEES.map((a) => ({ value: a, label: a }))}
                  value={filters.assignee}
                  onValueChange={(v) => setFilters((f) => ({ ...f, assignee: v ?? undefined }))}
                />
              </div>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button rank="secondary" leadingIcon="sort-descending">
                  {sortLabels[sort]}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {(Object.keys(sortLabels) as SortKey[]).map((k) => (
                  <DropdownMenuItem key={k} onSelect={() => setSort(k)}>
                    {sortLabels[k]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Active filter chips (channel + status) */}
          {(activeChips.length > 0 || hasAnyFilter) && (
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
              {activeChips.map((chip) => (
                <div
                  key={chip.key}
                  className="tw:inline-flex tw:items-center tw:gap-2 tw:rounded-lg tw:bg-primary-50 tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:text-primary-700"
                >
                  {chip.label}
                  <button aria-label={`Remove ${chip.label}`} onClick={chip.onRemove} className="tw:flex tw:items-center">
                    <Icon type="x-close" className="tw:h-3 tw:w-3" />
                  </button>
                </div>
              ))}
              {hasAnyFilter && (
                <Button rank="link" size="sm" leadingIcon="refresh" onClick={() => setFilters({ channels: [] })}>
                  {t.clearFilters}
                </Button>
              )}
            </div>
          )}

          {/* Inbox: list | conversation | details */}
          <div
            className={clsx(
              'tw:grid tw:h-[80vh] tw:min-h-[420px] tw:overflow-hidden tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:shadow-xs',
              readingPane
                ? railOpen
                  ? 'tw:grid-cols-[340px_1fr_300px]'
                  : 'tw:grid-cols-[340px_1fr_44px]'
                : view === 'list'
                  ? 'tw:grid-cols-1'
                  : railOpen
                    ? 'tw:grid-cols-[1fr_300px]'
                    : 'tw:grid-cols-[1fr_44px]'
            )}
          >
            {/* List column */}
            {(readingPane || view === 'list') && (
              <div className={clsx('tw:flex tw:min-h-0 tw:flex-col', readingPane && 'tw:border-r tw:border-gray-200')}>
                <div className="tw:flex tw:gap-1 tw:border-b tw:border-gray-200 tw:px-3 tw:pt-2">
                  <AudienceTab
                    active={audience === 'supporter'}
                    label={t.supporterInbox}
                    count={supporterCount}
                    countCls="tw:bg-primary-500"
                    activeCls="tw:border-primary-500 tw:text-primary-700"
                    onClick={() => setAudience('supporter')}
                  />
                  <AudienceTab
                    active={audience === 'guest'}
                    label={t.guestInbox}
                    count={guestCount}
                    activeStyle={gs.tab}
                    countStyle={gs.count}
                    onClick={() => setAudience('guest')}
                  />
                </div>

                <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto">
                  {results.length === 0 ? (
                    <div className="tw:p-8 tw:text-center tw:text-sm tw:text-gray-500">{t.noMatch}</div>
                  ) : (
                    pageItems.map(renderRow)
                  )}
                </div>

                <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:border-t tw:border-gray-200 tw:px-3 tw:py-2">
                  <span className="tw:text-xs tw:text-gray-500">
                    {`${results.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}-${Math.min(currentPage * PAGE_SIZE, results.length)} ${t.of} ${results.length}`}
                  </span>
                  {totalPages > 1 && <Pager page={currentPage} totalPages={totalPages} onPage={setPage} />}
                </div>
              </div>
            )}

            {/* Conversation column */}
            {(readingPane || view === 'thread') && (selected ? (
              <div className="tw:flex tw:min-h-0 tw:flex-col">
                <div className="tw:flex tw:items-center tw:gap-3 tw:border-b tw:border-gray-200 tw:bg-gray-50 tw:px-4 tw:py-3">
                  {!readingPane && (
                    <button
                      onClick={() => setView('list')}
                      aria-label={t.back}
                      className="tw:flex tw:items-center tw:gap-1 tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:px-2.5 tw:py-1.5 tw:text-sm tw:font-medium tw:text-gray-600 tw:hover:border-primary-400 tw:hover:text-primary-600"
                    >
                      <Icon type="arrow-narrow-left" className="tw:h-4 tw:w-4" />
                      {t.back}
                    </button>
                  )}
                  <ChannelGlyph channel={selected.channel} />
                  <div className="tw:min-w-0">
                    <div className="tw:flex tw:items-center tw:gap-1.5">
                      <span className="tw:text-sm tw:font-semibold tw:text-gray-900">{selected.name}</span>
                      <button
                        type="button"
                        onClick={() => setProfileOpen(true)}
                        title={selected.audience === 'guest' ? t.openGuest : t.openSupporter}
                        aria-label={selected.audience === 'guest' ? t.openGuest : t.openSupporter}
                        className="tw:text-gray-400 tw:hover:text-primary-600"
                      >
                        <Icon type="external-link" width={15} height={15} aria-hidden />
                      </button>
                    </div>
                    <div className="tw:truncate tw:text-xs tw:text-gray-500">
                      {selected.channel === 'email' ? (
                        <>
                          {selected.handle} ·{' '}
                          <ShortTime time={selected.time} minsAgo={selected.minsAgo} atWord={t.at} />
                        </>
                      ) : (
                        `${CHANNELS[selected.channel].label} · ${selected.handle}`
                      )}
                    </div>
                  </div>
                  <div className="tw:ml-auto tw:flex tw:items-center tw:gap-2">
                    <StatusPill c={selected} />
                    <button
                      onClick={() => setRailOpen((o) => !o)}
                      className="tw:flex tw:items-center tw:gap-1 tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:px-2.5 tw:py-1.5 tw:text-xs tw:font-medium tw:text-gray-600 tw:hover:border-primary-400 tw:hover:text-primary-600"
                    >
                      {railOpen ? t.hideActions : t.showActions}
                      <Icon type={railOpen ? 'chevron-right' : 'chevron-left'} className="tw:h-3.5 tw:w-3.5" />
                    </button>
                  </div>
                </div>

                {selected.channel === 'email' && (
                  <div className="tw:border-b tw:border-gray-200 tw:px-5 tw:py-3">
                    <div className="tw:text-lg tw:font-bold tw:text-gray-900">{selected.subject}</div>
                  </div>
                )}

                <div ref={threadScrollRef} className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:px-5 tw:py-4">
                  {selected.audience === 'guest' && <GuestBanner name={selected.name} gs={gs} />}
                  {selected.channel === 'email' ? <EmailThread c={selected} /> : <ChatThread c={selected} />}
                </div>

                <Composer channel={selected.channel} mode={composerMode} onMode={setComposerMode} />
              </div>
            ) : (
              <div className="tw:flex tw:items-center tw:justify-center tw:text-sm tw:text-gray-500">{t.selectConversation}</div>
            ))}

            {/* Details column (collapsible) */}
            {(readingPane || view === 'thread') &&
              (railOpen ? (
                selected && <ContextRail c={selected} onCollapse={() => setRailOpen(false)} onViewSupporter={() => setProfileOpen(true)} showAssignee={showAssignee} />
              ) : (
                <div className="tw:flex tw:items-start tw:justify-center tw:border-l tw:border-gray-200 tw:bg-gray-50 tw:py-3">
                  <button onClick={() => setRailOpen(true)} aria-label={t.showActions} className={ICON_BTN}>
                    <Icon type="chevron-left" className="tw:h-4 tw:w-4" />
                  </button>
                </div>
              ))}
          </div>

          {/* ----------------------------------------------------------------
              Design exploration: floating dev-only overlay, portaled to body
              so it sits at the viewport bottom-left over the sidebar.
              Toggleable so it can be hidden for screenshots. Not the product.
          ---------------------------------------------------------------- */}
          {selected && <SupporterDetailsSheet c={selected} open={profileOpen} onOpenChange={setProfileOpen} gs={gs} />}

          {createPortal(
            <>
              <button
                onClick={() => setShowDesign((s) => !s)}
                className="tw:fixed tw:bottom-4 tw:left-4 tw:z-50 tw:flex tw:items-center tw:gap-2 tw:rounded-full tw:border tw:border-gray-300 tw:bg-white tw:px-3.5 tw:py-2 tw:text-sm tw:font-medium tw:text-gray-600 tw:shadow-md tw:hover:border-primary-400 tw:hover:text-primary-600"
              >
                <Icon type="eye" className="tw:h-4 tw:w-4" />
                {showDesign ? 'Hide design tools' : 'Design tools'}
              </button>

              {showDesign && (
                <div className="tw:fixed tw:bottom-16 tw:left-4 tw:z-50 tw:w-[min(820px,calc(100vw-2rem))] tw:rounded-xl tw:border tw:border-dashed tw:border-gray-300 tw:bg-white tw:p-5 tw:shadow-xl">
                  <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2">
                    <Icon type="eye" className="tw:h-4 tw:w-4 tw:text-gray-400" />
                    <span className="tw:text-sm tw:font-bold tw:text-gray-700">Design exploration</span>
                    <button onClick={() => setShowDesign(false)} aria-label="Hide design tools" className={clsx('tw:ml-auto', ICON_BTN)}>
                      <Icon type="x-close" className="tw:h-4 tw:w-4" />
                    </button>
                  </div>
                  <p className="tw:mb-4 tw:text-xs tw:leading-relaxed tw:text-gray-500">
                    This panel is purely for exploring the design and is not part of the product.
                  </p>
                  <div className="tw:flex tw:flex-wrap tw:gap-8">
                    <SegControl
                      label="Reading pane"
                      value={readingPane ? 'side' : 'full'}
                      onChange={(v) => { setReadingPane(v === 'side'); setView('list') }}
                      options={[{ value: 'side', label: 'Side by side' }, { value: 'full', label: 'Full window' }]}
                    />
                    <SegControl
                      label="Density"
                      value={density}
                      onChange={(v) => setDensity(v as Density)}
                      options={[{ value: 'comfortable', label: 'Comfortable' }, { value: 'compact', label: 'Compact' }]}
                    />
                    <SegControl
                      label="WhatsApp (planned)"
                      value={showWhatsApp ? 'on' : 'off'}
                      onChange={(v) => setShowWhatsApp(v === 'on')}
                      options={[{ value: 'off', label: 'Hidden' }, { value: 'on', label: 'Shown' }]}
                    />
                    <SegControl
                      label="Assignee (planned)"
                      value={showAssignee ? 'on' : 'off'}
                      onChange={(v) => {
                        const on = v === 'on'
                        setShowAssignee(on)
                        if (!on) setFilters((f) => ({ ...f, assignee: undefined }))
                      }}
                      options={[{ value: 'off', label: 'Hidden' }, { value: 'on', label: 'Shown' }]}
                    />
                    <SegControl
                      label="Status style"
                      value={statusStyle}
                      onChange={(v) => setStatusStyle(v as StatusStyle)}
                      options={[
                        { value: 'icon', label: 'Status icon' },
                        { value: 'faded', label: 'Fade responded' },
                        { value: 'tint', label: 'Status tint' },
                        { value: 'pillOnly', label: 'Pill only' },
                      ]}
                    />
                    <div className="tw:flex tw:flex-col tw:gap-1.5">
                      <span className="tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-400">Guest colour</span>
                      <div className="tw:flex tw:items-center tw:gap-2">
                        <input
                          type="color"
                          value={guestHex}
                          onChange={(e) => setGuestHex(e.target.value)}
                          aria-label="Guest colour"
                          className="tw:h-9 tw:w-12 tw:cursor-pointer tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:p-0.5"
                        />
                        <span className="tw:font-mono tw:text-sm tw:text-gray-600">{guestHex.toUpperCase()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>,
            document.body
          )}
        </div>
      </PageLayout.Body>
    </PageLayout.Root>
  )
}

function AudienceTab({
  active, label, count, countCls, activeCls, activeStyle, countStyle, onClick,
}: {
  active: boolean; label: string; count: number
  countCls?: string; activeCls?: string; activeStyle?: CSSProperties; countStyle?: CSSProperties; onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      style={active ? activeStyle : undefined}
      className={clsx(
        'tw:-mb-px tw:flex tw:items-center tw:gap-2 tw:border-b-2 tw:px-3 tw:py-2.5 tw:text-sm tw:font-semibold',
        active ? (activeStyle ? '' : activeCls) : 'tw:border-transparent tw:text-gray-500 tw:hover:text-gray-800'
      )}
    >
      {label}
      <span
        style={countStyle}
        className={clsx('tw:rounded-full tw:px-2 tw:text-xs tw:font-bold', countStyle ? '' : clsx('tw:text-white', countCls))}
      >
        {count}
      </span>
    </button>
  )
}

function ConversationRow({
  c, selected, compact, statusStyle, gs, onClick,
}: {
  c: Conversation; selected: boolean; compact: boolean; statusStyle: StatusStyle; gs: GuestStyles; onClick: () => void
}) {
  const guestNonSel = c.audience === 'guest' && !selected
  const awaiting = c.status === 'awaiting'

  // Status treatments to compare. Selection always wins. Weight/contrast and a small
  // marker beat a full-row fill; "tint" is kept as the heavier option.
  const fadeDone = statusStyle === 'faded' && !awaiting && !selected
  const emphasise = statusStyle === 'faded' && awaiting && !selected
  const bg = selected
    ? 'tw:bg-gray-100'
    : statusStyle === 'tint' && awaiting
      ? 'tw:bg-warning-50 tw:hover:bg-warning-100'
      : 'tw:hover:bg-gray-50'

  const nameCls = clsx(
    'tw:truncate tw:text-sm',
    emphasise ? 'tw:font-semibold tw:text-gray-900' : fadeDone ? 'tw:font-normal tw:text-gray-500' : 'tw:font-medium tw:text-gray-900'
  )
  const subjectCls = clsx('tw:mt-0.5 tw:block tw:truncate tw:text-sm', fadeDone ? 'tw:text-gray-500' : 'tw:text-gray-900')
  const previewCls = clsx('tw:mt-0.5 tw:block tw:truncate tw:text-xs', fadeDone ? 'tw:text-gray-400' : 'tw:text-gray-500')
  const showMeta = c.audience === 'guest' || statusStyle !== 'icon'

  return (
    <button
      onClick={onClick}
      aria-current={selected ? 'true' : undefined}
      style={guestNonSel ? gs.rowBorder : undefined}
      className={clsx(
        'tw:flex tw:w-full tw:gap-3 tw:border-b tw:border-gray-100 tw:border-l-[3px] tw:px-4 tw:text-left tw:transition-colors',
        compact ? 'tw:py-2' : 'tw:py-3',
        // Guests keep their inline colour bar; everything else has no left bar.
        guestNonSel ? '' : 'tw:border-l-transparent',
        bg
      )}
    >
      {statusStyle === 'icon' && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span role="img" aria-label={statusText(c.status)} className="tw:flex tw:h-8 tw:w-4 tw:shrink-0 tw:cursor-default tw:items-center tw:justify-center">
                <Icon
                  type={awaiting ? 'dot-circle' : 'check-circle'}
                  className={clsx('tw:h-4 tw:w-4', awaiting ? 'tw:text-warning-500' : 'tw:text-success-600')}
                  aria-hidden
                />
              </span>
            </TooltipTrigger>
            <TooltipContent>{statusText(c.status)}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      <ChannelGlyph channel={c.channel} size="sm" />
      <span className="tw:min-w-0 tw:flex-1">
        <span className="tw:flex tw:items-center tw:gap-2">
          <span className={nameCls}>{c.name}</span>
          <ShortTime time={c.time} minsAgo={c.minsAgo} atWord={t.at} className="tw:ml-auto tw:shrink-0 tw:text-xs tw:text-gray-500" />
        </span>
        <span className={subjectCls}>{c.subject}</span>
        {!compact && <span className={previewCls}>{c.preview}</span>}
        {showMeta && (
          <span className={clsx('tw:flex tw:flex-wrap tw:items-center tw:gap-1.5', compact ? 'tw:mt-1' : 'tw:mt-2')}>
            {c.audience === 'guest' && <GuestPill gs={gs} />}
            {statusStyle !== 'icon' && <StatusPill c={c} />}
          </span>
        )}
      </span>
    </button>
  )
}

function Pager({ page, totalPages, onPage }: { page: number; totalPages: number; onPage: (p: number) => void }) {
  const nums = Array.from({ length: totalPages }, (_, i) => i + 1)
  const navCls = 'tw:flex tw:h-7 tw:w-7 tw:items-center tw:justify-center tw:rounded-full tw:text-gray-500 tw:hover:bg-gray-100 tw:disabled:opacity-40'
  return (
    <div className="tw:flex tw:items-center tw:gap-1">
      <button className={navCls} disabled={page === 1} onClick={() => onPage(page - 1)} aria-label="Previous page">
        <Icon type="arrow-narrow-left" className="tw:h-4 tw:w-4" />
      </button>
      {nums.map((n) => (
        <button
          key={n}
          onClick={() => onPage(n)}
          className={clsx(
            'tw:h-7 tw:w-7 tw:rounded-full tw:text-xs tw:font-bold',
            n === page ? 'tw:bg-primary-700 tw:text-white' : 'tw:bg-gray-100 tw:text-gray-900 tw:hover:bg-gray-200'
          )}
        >
          {n}
        </button>
      ))}
      <button className={navCls} disabled={page === totalPages} onClick={() => onPage(page + 1)} aria-label="Next page">
        <Icon type="arrow-narrow-right" className="tw:h-4 tw:w-4" />
      </button>
    </div>
  )
}

function EmailThread({ c }: { c: Conversation }) {
  const messages: EmailMessage[] = c.emails ?? [{ from: 'them', name: c.name, time: c.time, html: `<p>${c.preview}</p>` }]
  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      {messages.map((m, i) => (
        <EmailCard key={i} m={m} ours={m.from === 'us'} group={c.group} counterpart={c.name} />
      ))}
    </div>
  )
}

function EmailCard({ m, ours, group, counterpart }: { m: EmailMessage; ours: boolean; group: string; counterpart: string }) {
  return (
    <div className={clsx('tw:overflow-hidden tw:rounded-xl tw:border tw:shadow-xs', ours ? 'tw:border-primary-200 tw:bg-primary-50/40' : 'tw:border-gray-200 tw:bg-white')}>
      <div className={clsx('tw:border-b tw:px-4 tw:py-3', ours ? 'tw:border-primary-100 tw:bg-primary-50' : 'tw:border-gray-200 tw:bg-gray-50')}>
        <div className="tw:flex tw:items-center tw:gap-2">
          <div className="tw:text-sm tw:font-semibold tw:text-gray-900">{m.name}</div>
          {ours && (
            <span className="tw:rounded-full tw:bg-primary-100 tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium tw:text-primary-700">{t.yourTeam}</span>
          )}
        </div>
        <div className="tw:text-xs tw:text-gray-500">{ours ? t.to(counterpart) : t.to(group)} · <ShortTime time={m.time} atWord={t.at} /></div>
      </div>
      <div
        className="tw:px-4 tw:py-4 tw:text-sm tw:leading-relaxed tw:text-gray-800 tw:[&_b]:font-semibold tw:[&_li]:my-1 tw:[&_p]:mb-3 tw:[&_ul]:ml-5 tw:[&_ul]:list-disc"
        dangerouslySetInnerHTML={{ __html: m.html }}
      />
      {m.attachments && m.attachments.length > 0 && (
        <div className="tw:flex tw:flex-wrap tw:gap-2 tw:px-4 tw:pb-4">
          {m.attachments.map((a) => (
            <span key={a} className="tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-lg tw:border tw:border-gray-200 tw:bg-gray-50 tw:px-3 tw:py-1.5 tw:text-xs tw:text-gray-600">
              <Icon type="save-disk" className="tw:h-3.5 tw:w-3.5 tw:text-gray-400" />
              {a}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function ChatThread({ c }: { c: Conversation }) {
  const bubbles = c.bubbles ?? [{ dir: 'in' as const, text: c.preview }]
  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      {bubbles.map((b, i) => (
        <div key={i} className={clsx('tw:flex tw:flex-col', b.dir === 'out' ? 'tw:items-end' : 'tw:items-start')}>
          <div
            className={clsx(
              'tw:max-w-[75%] tw:rounded-2xl tw:px-3 tw:py-2 tw:text-sm tw:text-gray-900',
              b.dir === 'out'
                ? 'tw:rounded-br-sm tw:bg-primary-50'
                : c.channel === 'whatsapp'
                  ? 'tw:rounded-bl-sm tw:bg-success-50'
                  : 'tw:rounded-bl-sm tw:bg-gray-100'
            )}
          >
            {b.text}
          </div>
          <span className="tw:mt-0.5 tw:px-1 tw:text-[11px] tw:text-gray-400">{b.dir === 'out' ? t.yourTeam : c.name}</span>
        </div>
      ))}
      <ShortTime time={c.time} minsAgo={c.minsAgo} atWord={t.at} className="tw:self-center tw:pt-1 tw:text-xs tw:text-gray-400" />
    </div>
  )
}

function GuestBanner({ name, gs }: { name: string; gs: GuestStyles }) {
  return (
    <div style={gs.banner} className="tw:mb-4 tw:flex tw:items-start tw:gap-3 tw:rounded-xl tw:border tw:p-4">
      <span style={gs.bannerIcon} className="tw:mt-0.5 tw:shrink-0">
        <Icon type="info-circle" className="tw:h-5 tw:w-5" />
      </span>
      <div className="tw:flex-1">
        <div style={gs.bannerTitle} className="tw:text-sm tw:font-bold">{t.guestBannerTitle}</div>
        <div className="tw:mt-0.5 tw:text-sm tw:text-gray-700">{t.guestBannerBody(name)}</div>
      </div>
      <div className="tw:flex tw:flex-col tw:gap-2">
        <Button rank="primary" size="sm" leadingIcon="plus">{t.addToGroup}</Button>
        <Button rank="link" color="danger" size="sm">{t.markSpam}</Button>
      </div>
    </div>
  )
}

function Composer({
  channel, mode, onMode,
}: {
  channel: Channel; mode: 'reply' | 'note'; onMode: (m: 'reply' | 'note') => void
}) {
  const isEmail = channel === 'email'
  const meta = channel === 'sms' ? t.smsMeta : channel === 'whatsapp' ? t.whatsappMeta : ''
  return (
    <div className="tw:border-t tw:border-gray-200 tw:bg-gray-50 tw:px-4 tw:py-3">
      <div className="tw:mb-2 tw:flex tw:gap-2">
        <ComposerTab active={mode === 'reply'} label={isEmail ? t.reply : t.channelReply(CHANNELS[channel].label)} onClick={() => onMode('reply')} />
        <ComposerTab active={mode === 'note'} label={t.internalNote} note onClick={() => onMode('note')} />
      </div>
      <textarea
        className={clsx(
          'tw:min-h-14 tw:w-full tw:resize-y tw:rounded-lg tw:border tw:px-3 tw:py-2.5 tw:text-sm tw:focus:outline-hidden tw:focus:ring-4 tw:ring-primary-100',
          mode === 'note'
            ? 'tw:border-warning-300 tw:bg-warning-50 tw:text-warning-800 tw:placeholder-warning-600'
            : 'tw:border-gray-300 tw:bg-white tw:text-gray-900 tw:placeholder-gray-400 tw:focus:border-primary-400'
        )}
        placeholder={mode === 'note' ? t.noteVisible : isEmail ? t.writeReply : t.typeMessage}
      />
      <div className="tw:mt-2 tw:flex tw:items-center tw:gap-2">
        <Button rank="primary" size="sm" leadingIcon="send">{isEmail ? t.sendResponded : t.send}</Button>
        {isEmail && <Button rank="secondary" size="sm">{t.saveDraft}</Button>}
        {meta && <span className="tw:ml-auto tw:text-xs tw:text-gray-400">{meta}</span>}
      </div>
    </div>
  )
}

function ComposerTab({ active, label, note, onClick }: { active: boolean; label: string; note?: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        'tw:rounded-lg tw:border tw:px-3 tw:py-1.5 tw:text-xs tw:font-semibold',
        active
          ? note
            ? 'tw:border-warning-500 tw:bg-warning-500 tw:text-white'
            : 'tw:border-primary-500 tw:bg-primary-500 tw:text-white'
          : 'tw:border-gray-300 tw:bg-white tw:text-gray-600'
      )}
    >
      {label}
    </button>
  )
}

function RailAction({ icon, label, danger }: { icon: IconTypes; label: string; danger?: boolean }) {
  return (
    <button
      className={clsx(
        'tw:flex tw:w-full tw:items-center tw:gap-2 tw:rounded-lg tw:px-2.5 tw:py-2 tw:text-sm tw:font-medium tw:hover:bg-gray-50',
        danger ? 'tw:text-error-700' : 'tw:text-gray-700'
      )}
    >
      <Icon type={icon} className={clsx('tw:h-4 tw:w-4', danger ? 'tw:text-error-500' : 'tw:text-gray-400')} />
      {label}
    </button>
  )
}

function ContextRail({ c, onCollapse, onViewSupporter, showAssignee }: { c: Conversation; onCollapse: () => void; onViewSupporter: () => void; showAssignee: boolean }) {
  const guest = c.audience === 'guest'
  return (
    <div className="tw:flex tw:min-h-0 tw:flex-col tw:overflow-y-auto tw:border-l tw:border-gray-200 tw:bg-gray-50 tw:p-4">
      <div className="tw:mb-3">
        <div className="tw:flex tw:items-center">
          <span className="tw:text-sm tw:font-semibold tw:text-gray-700">{t.conversationDetails}</span>
          <button onClick={onCollapse} aria-label={t.hideActions} className={clsx('tw:ml-auto', ICON_BTN)}>
            <Icon type="chevron-right" className="tw:h-4 tw:w-4" />
          </button>
        </div>
        <div className="tw:mt-1 tw:text-xs tw:text-gray-500">
          in{' '}
          <a
            href="#"
            className="tw:font-medium tw:text-primary-700 tw:border-b tw:border-transparent tw:hover:text-primary-900 tw:hover:border-current tw:transition-colors tw:duration-200"
          >
            {c.group}
          </a>
        </div>
      </div>

      <Button rank="secondary" size="sm" onClick={onViewSupporter} className="tw:w-full tw:justify-center">
        {guest ? t.viewGuest : t.viewSupporter}
      </Button>
      {c.status === 'responded' ? (
        <Button rank="secondary" size="sm" leadingIcon="flip-backwards" className="tw:mt-2 tw:w-full tw:justify-center">
          {t.markAwaiting}
        </Button>
      ) : (
        <Button rank="primary" size="sm" leadingIcon="check" className="tw:mt-2 tw:w-full tw:justify-center">
          {t.markResponded}
        </Button>
      )}

      {showAssignee && (
        <>
          <RailHeading>{t.assignee}</RailHeading>
          <div className="tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:p-3 tw:shadow-xs">
            <div className="tw:flex tw:justify-between tw:text-sm">
              <span className="tw:text-gray-500">{t.owner}</span>
              <span className="tw:font-semibold tw:text-gray-900">{c.assignee}</span>
            </div>
            <button className="tw:mt-2 tw:flex tw:w-full tw:items-center tw:justify-between tw:rounded-lg tw:border tw:border-gray-300 tw:bg-gray-50 tw:px-3 tw:py-2 tw:text-sm tw:text-gray-700">
              {t.assignTo}
              <Icon type="chevron-down" className="tw:h-4 tw:w-4 tw:text-gray-400" />
            </button>
          </div>
        </>
      )}

      <RailHeading>{t.quickActions}</RailHeading>
      <div className="tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:p-1.5 tw:shadow-xs">
        {guest ? (
          <>
            <RailAction icon="plus" label={t.addToGroup} />
            <RailAction icon="pencil-02" label={t.addTag} />
            <RailAction icon="book-open-01" label={t.logContact} />
            <RailAction icon="trash-01" label={t.markSpam} danger />
          </>
        ) : (
          <>
            <RailAction icon="pencil-02" label={t.addTag} />
            <RailAction icon="book-open-01" label={t.logContact} />
            <RailAction icon="phone-call-01" label={t.addToCallList} />
          </>
        )}
      </div>

      <RailHeading>{t.recentActivity}</RailHeading>
      <div className="tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:p-3 tw:shadow-xs">
        <TimelineItem title={t.replied(c.name)} sub={<><ShortTime time={c.time} minsAgo={c.minsAgo} atWord={t.at} /> · {t.via(CHANNELS[c.channel].label)}</>} />
        {showAssignee && (
          <TimelineItem
            title={c.assignee === 'Unassigned' ? t.unassigned : t.assignedTo(c.assignee)}
            sub={t.onThisThread}
          />
        )}
      </div>
    </div>
  )
}

function RailHeading({ children }: { children: string }) {
  return <h4 className="tw:mb-2 tw:mt-4 tw:text-sm tw:font-semibold tw:text-gray-700">{children}</h4>
}

function TimelineItem({ title, sub }: { title: string; sub: ReactNode }) {
  return (
    <div className="tw:flex tw:items-start tw:gap-2.5 tw:py-1">
      <span className="tw:mt-1.5 tw:h-1.5 tw:w-1.5 tw:shrink-0 tw:rounded-full tw:bg-primary-500" />
      <div>
        <div className="tw:text-sm tw:font-semibold tw:text-gray-900">{title}</div>
        <div className="tw:text-xs tw:text-gray-500">{sub}</div>
      </div>
    </div>
  )
}

function SegControl({
  label, value, options, onChange,
}: {
  label: string; value: string; options: Array<{ value: string; label: string }>; onChange: (v: string) => void
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5">
      <span className="tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-400">{label}</span>
      <div className="tw:inline-flex tw:w-fit tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-300">
        {options.map((o, i) => (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            aria-pressed={value === o.value}
            className={clsx(
              'tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium',
              i > 0 && 'tw:border-l tw:border-gray-300',
              value === o.value ? 'tw:bg-primary-50 tw:text-primary-700' : 'tw:bg-white tw:text-gray-600 tw:hover:bg-gray-50'
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

const PROFILE_TAGS = ['Volunteer', 'Donor', 'Event host', 'Door-knocker', 'Local organiser', 'Newsletter']

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter((p) => /[A-Za-z]/.test(p))
  return parts.length ? parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('') : '?'
}

function derivedEmail(name: string): string | undefined {
  const parts = name.toLowerCase().split(/\s+/).filter((p) => /[a-z]/.test(p))
  if (parts.length >= 2) return `${parts[0][0]}.${parts[parts.length - 1]}@gmail.com`
  if (parts.length === 1) return `${parts[0]}@gmail.com`
  return undefined
}

// Plausible, stable sample details derived from a conversation, so the profile
// reads like a real supporter record instead of a copy of the inbox row. The
// real SupporterProfileSheet is bound to live GraphQL data; this stand-in reuses
// the same v2 Sheet atom so the slide-in matches the product.
function sampleProfile(c: Conversation) {
  const fields = [c.handle, c.name]
  const email = fields.find((v) => v.includes('@')) ?? derivedEmail(c.name)
  const phone = fields.find((v) => /^\+?[\d ]+$/.test(v.trim())) ?? `+1 555 ${String(1000 + c.id * 7).slice(-4)}`
  const place = c.group.replace(/^Common Ground\s+/, '')
  const tags = c.audience === 'guest'
    ? ['Not a member yet']
    : Array.from(new Set([PROFILE_TAGS[c.id % PROFILE_TAGS.length], PROFILE_TAGS[(c.id * 3 + 2) % PROFILE_TAGS.length]]))
  return { email, phone, place, since: 2018 + (c.id % 6), tags }
}

function ProfileRow({ icon, label, value }: { icon: IconTypes; label: string; value: ReactNode }) {
  return (
    <div className="tw:flex tw:items-center tw:gap-3 tw:border-t tw:border-gray-100 tw:px-4 tw:py-3 tw:text-sm tw:first:border-t-0">
      <Icon type={icon} className="tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-400" aria-hidden />
      <span className="tw:text-gray-500">{label}</span>
      <span className="tw:ml-auto tw:min-w-0 tw:truncate tw:font-medium tw:text-gray-900">{value}</span>
    </div>
  )
}

function SupporterDetailsSheet({ c, open, onOpenChange, gs }: { c: Conversation; open: boolean; onOpenChange: (open: boolean) => void; gs: GuestStyles }) {
  const guest = c.audience === 'guest'
  const p = sampleProfile(c)
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent aria-describedby={undefined}>
        <SheetTitle>{c.name}</SheetTitle>
        <div className="tw:flex tw:flex-col tw:gap-5 tw:p-6">
          <div className="tw:flex tw:items-center tw:gap-3">
            <span
              style={guest ? gs.pill : undefined}
              className={clsx(
                'tw:flex tw:h-12 tw:w-12 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-base tw:font-semibold',
                guest ? '' : 'tw:bg-primary-100 tw:text-primary-700'
              )}
            >
              {initialsOf(c.name)}
            </span>
            <div className="tw:min-w-0">
              <div className="tw:truncate tw:text-base tw:font-semibold tw:text-gray-900">{c.name}</div>
              <div className="tw:text-sm tw:text-gray-500">{`${guest ? t.guest : t.supporter} · ${p.place}`}</div>
            </div>
          </div>

          <div className="tw:flex tw:flex-wrap tw:gap-1.5">
            {p.tags.map((tag) => (
              <span key={tag} className="tw:inline-flex tw:items-center tw:gap-1 tw:rounded-full tw:bg-gray-100 tw:px-2.5 tw:py-1 tw:text-xs tw:font-medium tw:text-gray-700">
                <Icon type="tag-01" className="tw:h-3 tw:w-3 tw:text-gray-400" aria-hidden />
                {tag}
              </span>
            ))}
          </div>

          <div className="tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:shadow-xs">
            <ProfileRow icon="mail-01" label="Email" value={p.email ?? 'Not provided'} />
            <ProfileRow icon="phone-call-01" label="Phone" value={p.phone} />
            <ProfileRow icon="location" label="Location" value={p.place} />
            <ProfileRow icon="users-01" label={t.group} value={c.group} />
            {!guest && <ProfileRow icon="calendar" label="Supporter since" value={String(p.since)} />}
            <ProfileRow icon="clock" label="Last contact" value={<ShortTime time={c.time} minsAgo={c.minsAgo} atWord={t.at} />} />
          </div>

          <p className="tw:text-xs tw:text-gray-400">{t.sampleProfile}</p>

          <div className="tw:flex tw:justify-end">
            <Button rank="secondary" onClick={() => onOpenChange(false)}>{t.close}</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default GroupInboxIndexGeneric
